/**
 * 纵横杯 16 环节改良奥瑞冈制赛程表。
 *
 * 结构：三轮申论与单向质询（6 组 = 12 环节）+ 两轮 1v1 对辩（2 环节）
 *       + 双四辩结辩（2 环节）= 16 环节。
 *
 * 时间口径（训练模式，可在教师端覆写）：
 * - 申论：一辩 3:00 / 二辩 2:30 / 三辩 2:00，单向计时；
 * - 单向质询：双方各 60s 双表 Chess Clock 交替（合计 2:00）；
 * - 1v1 对辩：双方各 60s 双表 Chess Clock（合计 2:00），开表前 30s 静默读卡；
 * - 结辩：3:00 单向计时。
 */
import type { CardSlot, PairDealPlan, Side, StageDefinition } from './types.ts';

const stages: StageDefinition[] = [
  {
    index: 1,
    id: 'AFF_1AC',
    name: '正方一辩申论',
    shortName: '正一申论',
    kind: 'CONSTRUCTIVE',
    speakerSide: 'AFF',
    speakerRole: '正方一辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 180, NEG: 0 },
    startSide: 'AFF',
    cardSlot: 'CONSTRUCTIVE',
    pairId: 'P1',
    prepSec: 0,
    allowToggle: false,
  },
  {
    index: 2,
    id: 'NEG_2_CX',
    name: '反方二辩单向质询',
    shortName: '反二质询',
    kind: 'CROSS_EXAM',
    speakerSide: 'NEG',
    speakerRole: '反方二辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'NEG',
    cardSlot: 'CROSS_EXAM',
    pairId: 'P1',
    prepSec: 0,
    allowToggle: true,
  },
  {
    index: 3,
    id: 'NEG_1NC',
    name: '反方一辩申论',
    shortName: '反一申论',
    kind: 'CONSTRUCTIVE',
    speakerSide: 'NEG',
    speakerRole: '反方一辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 0, NEG: 180 },
    startSide: 'NEG',
    cardSlot: 'CONSTRUCTIVE',
    pairId: 'P2',
    prepSec: 0,
    allowToggle: false,
  },
  {
    index: 4,
    id: 'AFF_2_CX',
    name: '正方二辩单向质询',
    shortName: '正二质询',
    kind: 'CROSS_EXAM',
    speakerSide: 'AFF',
    speakerRole: '正方二辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'AFF',
    cardSlot: 'CROSS_EXAM',
    pairId: 'P2',
    prepSec: 0,
    allowToggle: true,
  },
  {
    index: 5,
    id: 'AFF_2AC',
    name: '正方二辩申论',
    shortName: '正二申论',
    kind: 'CONSTRUCTIVE',
    speakerSide: 'AFF',
    speakerRole: '正方二辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 150, NEG: 0 },
    startSide: 'AFF',
    cardSlot: 'CONSTRUCTIVE',
    pairId: 'P3',
    prepSec: 0,
    allowToggle: false,
  },
  {
    index: 6,
    id: 'NEG_1_CX',
    name: '反方一辩单向质询',
    shortName: '反一质询',
    kind: 'CROSS_EXAM',
    speakerSide: 'NEG',
    speakerRole: '反方一辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'NEG',
    cardSlot: 'CROSS_EXAM',
    pairId: 'P3',
    prepSec: 0,
    allowToggle: true,
  },
  {
    index: 7,
    id: 'NEG_2NC',
    name: '反方二辩申论',
    shortName: '反二申论',
    kind: 'CONSTRUCTIVE',
    speakerSide: 'NEG',
    speakerRole: '反方二辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 0, NEG: 150 },
    startSide: 'NEG',
    cardSlot: 'CONSTRUCTIVE',
    pairId: 'P4',
    prepSec: 0,
    allowToggle: false,
  },
  {
    index: 8,
    id: 'AFF_3_CX',
    name: '正方三辩单向质询',
    shortName: '正三质询',
    kind: 'CROSS_EXAM',
    speakerSide: 'AFF',
    speakerRole: '正方三辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'AFF',
    cardSlot: 'CROSS_EXAM',
    pairId: 'P4',
    prepSec: 0,
    allowToggle: true,
  },
  {
    index: 9,
    id: 'AFF_3AC',
    name: '正方三辩申论',
    shortName: '正三申论',
    kind: 'CONSTRUCTIVE',
    speakerSide: 'AFF',
    speakerRole: '正方三辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 120, NEG: 0 },
    startSide: 'AFF',
    cardSlot: 'CONSTRUCTIVE',
    pairId: 'P5',
    prepSec: 0,
    allowToggle: false,
  },
  {
    index: 10,
    id: 'NEG_3_CX',
    name: '反方三辩单向质询',
    shortName: '反三质询',
    kind: 'CROSS_EXAM',
    speakerSide: 'NEG',
    speakerRole: '反方三辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'NEG',
    cardSlot: 'CROSS_EXAM',
    pairId: 'P5',
    prepSec: 0,
    allowToggle: true,
  },
  {
    index: 11,
    id: 'NEG_3NC',
    name: '反方三辩申论',
    shortName: '反三申论',
    kind: 'CONSTRUCTIVE',
    speakerSide: 'NEG',
    speakerRole: '反方三辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 0, NEG: 120 },
    startSide: 'NEG',
    cardSlot: 'CONSTRUCTIVE',
    pairId: 'P6',
    prepSec: 0,
    allowToggle: false,
  },
  {
    index: 12,
    id: 'AFF_1_CX',
    name: '正方一辩单向质询',
    shortName: '正一质询',
    kind: 'CROSS_EXAM',
    speakerSide: 'AFF',
    speakerRole: '正方一辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'AFF',
    cardSlot: 'CROSS_EXAM',
    pairId: 'P6',
    prepSec: 0,
    allowToggle: true,
  },
  {
    index: 13,
    id: 'DUEL_1',
    name: '第一轮 1v1 对辩',
    shortName: '对辩一轮',
    kind: 'DUEL',
    speakerSide: 'AFF',
    speakerRole: '正反四辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'AFF',
    cardSlot: 'SANDBOX',
    pairId: 'D1',
    prepSec: 30,
    allowToggle: true,
  },
  {
    index: 14,
    id: 'DUEL_2',
    name: '第二轮 1v1 对辩',
    shortName: '对辩二轮',
    kind: 'DUEL',
    speakerSide: 'NEG',
    speakerRole: '正反四辩',
    clockMode: 'CHESS',
    budgets: { AFF: 60, NEG: 60 },
    startSide: 'NEG',
    cardSlot: 'SANDBOX',
    pairId: 'D2',
    prepSec: 30,
    allowToggle: true,
  },
  {
    index: 15,
    id: 'NEG_FINAL',
    name: '反方四辩结辩',
    shortName: '反四结辩',
    kind: 'FINAL_REBUTTAL',
    speakerSide: 'NEG',
    speakerRole: '反方四辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 0, NEG: 180 },
    startSide: 'NEG',
    cardSlot: null,
    pairId: null,
    prepSec: 0,
    allowToggle: false,
  },
  {
    index: 16,
    id: 'AFF_FINAL',
    name: '正方四辩结辩',
    shortName: '正四结辩',
    kind: 'FINAL_REBUTTAL',
    speakerSide: 'AFF',
    speakerRole: '正方四辩',
    clockMode: 'SINGLE',
    budgets: { AFF: 180, NEG: 0 },
    startSide: 'AFF',
    cardSlot: null,
    pairId: null,
    prepSec: 0,
    allowToggle: false,
  },
];

export const STAGE_SCHEDULE: readonly StageDefinition[] = Object.freeze(stages);

export function getStage(stageIndex: number): StageDefinition {
  const stage = STAGE_SCHEDULE[stageIndex - 1];
  if (!stage) throw new Error(`非法环节序号: ${stageIndex}`);
  return stage;
}

export function getFirstStage(): StageDefinition {
  return getStage(1);
}

export function getNextStageIndex(stageIndex: number): number | null {
  return stageIndex >= STAGE_SCHEDULE.length ? null : stageIndex + 1;
}

/** 一个双盲配对内的两个环节（如 P1 = 环节 1 + 环节 2） */
export function getPairStages(pairId: string): StageDefinition[] {
  return STAGE_SCHEDULE.filter((s) => s.pairId === pairId);
}

/** 全部发牌配对计划（6 组申论+质询、2 个 1v1 沙盘） */
export function getPairDealPlans(): PairDealPlan[] {
  const byPair = new Map<string, StageDefinition[]>();
  for (const stage of STAGE_SCHEDULE) {
    if (!stage.pairId) continue;
    const list = byPair.get(stage.pairId) ?? [];
    list.push(stage);
    byPair.set(stage.pairId, list);
  }
  const plans: PairDealPlan[] = [];
  for (const [pairId, pairStages] of byPair) {
    const constructive = pairStages.find((s) => s.cardSlot === 'CONSTRUCTIVE');
    const sandbox = pairStages.some((s) => s.cardSlot === 'SANDBOX');
    plans.push({
      pairId,
      stageIndices: pairStages.map((s) => s.index).sort((a, b) => a - b),
      constructiveSide: sandbox ? null : (constructive?.speakerSide ?? null),
      sandbox,
    });
  }
  return plans.sort((a, b) => (a.stageIndices[0] ?? 0) - (b.stageIndices[0] ?? 0));
}

/** 某环节对应的发牌配对计划（无卡片环节返回 null） */
export function getPairPlanForStage(stageIndex: number): PairDealPlan | null {
  const stage = getStage(stageIndex);
  if (!stage.pairId) return null;
  return getPairDealPlans().find((p) => p.pairId === stage.pairId) ?? null;
}

/** 某环节应当收到卡片的一侧（沙盘为双方，其余为发言/质询方） */
export function getCardReceivingSides(stageIndex: number): Side[] {
  const stage = getStage(stageIndex);
  if (!stage.cardSlot) return [];
  if (stage.cardSlot === 'SANDBOX') return ['AFF', 'NEG'];
  return [stage.speakerSide];
}

/** 某环节开始时是否需要发牌（配对内的第一个环节负责承诺+发申论卡） */
export function isPairOpeningStage(stageIndex: number): boolean {
  const plan = getPairPlanForStage(stageIndex);
  if (!plan) return false;
  return plan.stageIndices[0] === stageIndex;
}

/** 配对结束（揭示）环节：配对内最后一个环节 */
export function isPairClosingStage(stageIndex: number): boolean {
  const plan = getPairPlanForStage(stageIndex);
  if (!plan) return false;
  return plan.stageIndices[plan.stageIndices.length - 1] === stageIndex;
}

/** 环节默认总时长（双方预算之和），供 UI 展示 */
export function stageTotalSeconds(stage: StageDefinition): number {
  return stage.budgets.AFF + stage.budgets.NEG;
}

/** 校验赛程表完整性：16 环节、配对一致、时间预算非负 */
export function validateSchedule(schedule: readonly StageDefinition[] = STAGE_SCHEDULE): string[] {
  const problems: string[] = [];
  if (schedule.length !== 16) problems.push(`环节数量应为 16，实际 ${schedule.length}`);
  schedule.forEach((stage, i) => {
    if (stage.index !== i + 1) problems.push(`环节序号不连续：期望 ${i + 1}，实际 ${stage.index}`);
    if (stage.budgets.AFF < 0 || stage.budgets.NEG < 0) problems.push(`环节 ${stage.index} 时间为负`);
    if (stage.clockMode === 'SINGLE' && stage.budgets[stage.speakerSide] <= 0) {
      problems.push(`环节 ${stage.index} SINGLE 模式下发言方预算应为正`);
    }
    if (stage.cardSlot && !stage.pairId) problems.push(`环节 ${stage.index} 有卡片但没有配对`);
  });
  const plans = getPairDealPlans();
  for (const plan of plans) {
    if (plan.sandbox && plan.stageIndices.length !== 1) {
      problems.push(`沙盘配对 ${plan.pairId} 应只包含 1 个环节`);
    }
    if (!plan.sandbox && plan.stageIndices.length !== 2) {
      problems.push(`申论质询配对 ${plan.pairId} 应包含 2 个环节`);
    }
    if (!plan.sandbox) {
      const pairStages = plan.stageIndices.map(getStage);
      const constructive = pairStages.find((s) => s.cardSlot === 'CONSTRUCTIVE');
      const crossExam = pairStages.find((s) => s.cardSlot === 'CROSS_EXAM');
      if (!constructive || !crossExam) problems.push(`配对 ${plan.pairId} 缺申论卡或质询卡环节`);
      if (constructive && crossExam && constructive.speakerSide === crossExam.speakerSide) {
        problems.push(`配对 ${plan.pairId} 申论与质询应为不同阵营`);
      }
    }
  }
  return problems;
}
